/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useRef, useState } from "react";
import { api, formatApiError, BACKEND_URL, TOKEN_KEY } from "../lib/api";
import { toast } from "sonner";
import {
  FloppyDisk, Plus, Trash, ImageSquare, ArrowsOutCardinal, FileArrowDown,
  ArrowCounterClockwise, X, GridFour, Signature, Stack,
} from "@phosphor-icons/react";

// A4 landscape in points; we use 1pt ≈ 1px in the editor.
const PAGE_W = 842;
const PAGE_H = 595;
const GRID_OPTIONS = [
  { value: 0, label: "Off" },
  { value: 5, label: "5" },
  { value: 10, label: "10" },
  { value: 20, label: "20" },
];

const snap = (v, grid) => (grid > 0 ? Math.round(v / grid) * grid : v);

const PLACEHOLDERS = [
  "{{student_name}}", "{{student_id}}", "{{course_name}}", "{{course_code}}",
  "{{company_name}}", "{{company_address}}", "{{role_title}}",
  "{{start_date}}", "{{end_date}}", "{{duration}}",
  "{{institute_name}}", "{{institute_address}}", "{{certificate_no}}", "{{issue_date}}",
];

const ALIGN_OPTIONS = [
  { id: "left", label: "L" }, { id: "center", label: "C" }, { id: "right", label: "R" },
];

const DEFAULT_ELEMENTS = [
  { id: "title", text: "Certificate of Placement", x: 271, y: 110, w: 300, font_size: 32, font_weight: "bold", color: "#0055FF", align: "center" },
  { id: "body", text: "This is to certify that {{student_name}} ({{student_id}}), a student of {{course_name}} at {{institute_name}}, has successfully completed the placement programme at {{company_name}} from {{start_date}} to {{end_date}} ({{duration}}).", x: 90, y: 230, w: 660, font_size: 14, font_weight: "normal", color: "#0F172A", align: "center" },
  { id: "issued", text: "Issued on {{issue_date}}", x: 90, y: 360, w: 660, font_size: 12, font_weight: "normal", color: "#64748B", align: "center" },
  { id: "sig-name", text: "Dr. Jane Doe", x: 600, y: 480, w: 200, font_size: 12, font_weight: "bold", color: "#0F172A", align: "right" },
  { id: "sig-title", text: "Principal · {{institute_name}}", x: 540, y: 500, w: 260, font_size: 10, font_weight: "normal", color: "#64748B", align: "right" },
  { id: "stamp", text: "{{student_id}}", x: 60, y: 480, w: 200, font_size: 14, font_weight: "bold", color: "#B45309", align: "left" },
];

const STANDARD_DEFAULT_ELEMENTS = [
  { id: "title", kind: "text", text: "Certificate of Completion", x: 271, y: 110, w: 300, font_size: 32, font_weight: "bold", color: "#0055FF", align: "center" },
  { id: "body", kind: "text", text: "This is to certify that {{student_name}} ({{student_id}}) has successfully completed the {{course_name}} programme at {{institute_name}}.", x: 90, y: 230, w: 660, font_size: 14, font_weight: "normal", color: "#0F172A", align: "center" },
  { id: "issued", kind: "text", text: "Issued on {{issue_date}}", x: 90, y: 360, w: 660, font_size: 12, font_weight: "normal", color: "#64748B", align: "center" },
  { id: "stamp", kind: "text", text: "{{student_id}}", x: 60, y: 480, w: 200, font_size: 14, font_weight: "bold", color: "#B45309", align: "left" },
];

const KIND_DEFAULTS = {
  placement: { title: "Certificate of Placement", elements: DEFAULT_ELEMENTS },
  standard:  { title: "Certificate of Completion", elements: STANDARD_DEFAULT_ELEMENTS },
};

let _idCounter = 0;
const newId = () => `el-${Date.now()}-${++_idCounter}`;

export default function VisualCertificateEditor() {
  const [kind, setKind] = useState("placement"); // "placement" | "standard"
  const [template, setTemplate] = useState({
    title: "Certificate of Placement",
    elements: [],
    background_image_base64: null,
    show_default_frame: true,
    signatory_name: "",
    signatory_title: "",
    body: "",
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [grid, setGrid] = useState(10); // snap step in points; 0 = off
  const canvasRef = useRef(null);
  const [scale, setScale] = useState(1);
  const [libraryOpen, setLibraryOpen] = useState(false);
  const [library, setLibrary] = useState([]);
  const [libraryLoading, setLibraryLoading] = useState(false);

  const loadTemplate = (k) => {
    setLoading(true);
    return api.get(`/placements/template?kind=${k}`).then((r) => {
      setTemplate({
        title: r.data.title || KIND_DEFAULTS[k]?.title || "Certificate",
        elements: Array.isArray(r.data.elements) ? r.data.elements : [],
        background_image_base64: r.data.background_image_base64 || null,
        show_default_frame: r.data.show_default_frame !== false,
        signatory_name: r.data.signatory_name || "",
        signatory_title: r.data.signatory_title || "",
        body: r.data.body || "",
      });
      setSelectedId(null);
    }).finally(() => setLoading(false));
  };

  useEffect(() => {
    loadTemplate(kind);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kind]);

  const openLibrary = async () => {
    setLibraryOpen(true);
    if (library.length === 0) {
      setLibraryLoading(true);
      try {
        const r = await api.get("/settings/cert-images");
        setLibrary(r.data?.items || []);
      } catch (e) {
        toast.error("Could not load image library");
      } finally {
        setLibraryLoading(false);
      }
    }
  };

  const insertLibraryImage = (item) => {
    const el = {
      id: newId(),
      kind: "image",
      label: item.label,
      image_base64: item.image_base64,
      x: 60, y: 60, w: 140, h: 80,
    };
    setTemplate((t) => ({ ...t, elements: [...t.elements, el] }));
    setSelectedId(el.id);
    setLibraryOpen(false);
    toast.success(`Inserted ${item.label} — drag/resize on canvas`);
  };

  useEffect(() => {
    const onResize = () => {
      if (!canvasRef.current) return;
      const w = canvasRef.current.parentElement?.clientWidth || PAGE_W;
      setScale(Math.min(1, (w - 4) / PAGE_W));
    };
    onResize();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [loading]);

  const selected = template.elements.find((e) => e.id === selectedId);

  const updateEl = (id, patch) => {
    setTemplate((t) => ({
      ...t,
      elements: t.elements.map((e) => (e.id === id ? { ...e, ...patch } : e)),
    }));
  };

  const addText = () => {
    const el = {
      id: newId(),
      kind: "text",
      text: "New text",
      x: 100, y: 100, w: 240,
      font_size: 16, font_weight: "normal", color: "#0F172A", align: "left",
    };
    setTemplate((t) => ({ ...t, elements: [...t.elements, el] }));
    setSelectedId(el.id);
  };

  const sigInputRef = useRef(null);
  const [replacingId, setReplacingId] = useState(null);
  const addSignature = () => { setReplacingId(null); sigInputRef.current?.click(); };
  const replaceSignature = () => { setReplacingId(selectedId); sigInputRef.current?.click(); };
  const onSignaturePicked = (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Signature must be an image"); return; }
    if (file.size > 2 * 1024 * 1024) { toast.error("Signature under 2 MB"); return; }
    const reader = new FileReader();
    reader.onload = () => {
      if (replacingId) {
        // Replace existing element's image
        updateEl(replacingId, { image_base64: reader.result });
        toast.success("Signature replaced");
      } else {
        const el = {
          id: newId(),
          kind: "image",
          label: "Signature",
          image_base64: reader.result,
          x: 580, y: 460, w: 180, h: 60,
        };
        setTemplate((t) => ({ ...t, elements: [...t.elements, el] }));
        setSelectedId(el.id);
        toast.success("Signature added — drag/resize on canvas");
      }
      setReplacingId(null);
    };
    reader.readAsDataURL(file);
    if (sigInputRef.current) sigInputRef.current.value = "";
  };

  const deleteEl = (id) => {
    setTemplate((t) => ({ ...t, elements: t.elements.filter((e) => e.id !== id) }));
    if (selectedId === id) setSelectedId(null);
  };

  const insertPlaceholder = (ph) => {
    if (!selected) { toast.info("Select a text element first"); return; }
    updateEl(selected.id, { text: `${selected.text}${selected.text && !selected.text.endsWith(" ") ? " " : ""}${ph}` });
  };

  const onBackground = (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Background must be an image"); return; }
    if (file.size > 4 * 1024 * 1024) { toast.error("Background under 4 MB"); return; }
    const reader = new FileReader();
    reader.onload = () => setTemplate((t) => ({ ...t, background_image_base64: reader.result, show_default_frame: false }));
    reader.readAsDataURL(file);
  };

  const resetLayout = () => {
    if (!window.confirm("Reset to the default layout? Your customisations will be lost.")) return;
    const defs = KIND_DEFAULTS[kind] || KIND_DEFAULTS.placement;
    setTemplate((t) => ({
      ...t,
      title: defs.title,
      elements: defs.elements.map((e) => ({ ...e })),
      background_image_base64: null,
      show_default_frame: true,
    }));
    setSelectedId(null);
    toast.success("Layout reset · click 'Save template' to persist");
  };

  const save = async () => {
    if (template.elements.length === 0) { toast.error("Add at least one text element"); return; }
    setSaving(true);
    try {
      await api.put(`/placements/template?kind=${kind}`, template);
      toast.success(`${kind === "standard" ? "Standard" : "Placement"} template saved`);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  const previewPdf = async () => {
    const t = localStorage.getItem(TOKEN_KEY);
    // Save first so the preview reflects what's on screen
    await save();
    if (kind === "standard") {
      // Pick any enrolled student to preview the standard cert against
      try {
        const r = await api.get("/registrations?status=enrolled");
        const reg = (r.data || [])[0];
        if (!reg) { toast.info("No enrolled students yet — enrol one to preview"); return; }
        const res = await fetch(`${BACKEND_URL}/api/registrations/${reg.id}/certificate.pdf`, { headers: { Authorization: `Bearer ${t}` } });
        if (!res.ok) { toast.error("Preview failed"); return; }
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        window.open(url, "_blank", "noopener,noreferrer");
        setTimeout(() => URL.revokeObjectURL(url), 60000);
      } catch (e) {
        toast.error("Could not generate preview");
      }
      return;
    }
    // Placement preview: needs any placement record
    try {
      const r = await api.get("/placements");
      const p = (r.data || []).find((x) => x.status === "approved") || (r.data || [])[0];
      if (!p) { toast.info("No placements yet — create one to preview"); return; }
      const res = await fetch(`${BACKEND_URL}/api/placements/${p.id}/certificate.pdf`, { headers: { Authorization: `Bearer ${t}` } });
      if (!res.ok) { toast.error("Preview failed"); return; }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank", "noopener,noreferrer");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      toast.error("Could not generate preview");
    }
  };

  if (loading) return <div className="text-sm text-slate-400 font-mono-sms uppercase tracking-[0.2em]">Loading…</div>;

  return (
    <div className="border border-slate-200" data-testid="visual-cert-editor">
      {/* Kind selector */}
      <div className="flex items-center justify-between gap-3 px-5 py-3 border-b border-slate-200 bg-white">
        <div className="flex items-center gap-2">
          <span className="font-mono-sms text-[10px] uppercase tracking-[0.2em] text-slate-500">Template type</span>
          <div className="flex border border-slate-200" data-testid="template-kind-toggle">
            {["placement", "standard"].map((k) => (
              <button
                key={k}
                type="button"
                onClick={() => setKind(k)}
                data-testid={`kind-${k}`}
                className={`px-3 py-1 text-[10px] font-mono-sms uppercase tracking-[0.2em] ${
                  kind === k ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-50"
                }`}
              >
                {k === "placement" ? "With placement" : "Standard (no placement)"}
              </button>
            ))}
          </div>
        </div>
        <div className="font-mono-sms text-[10px] uppercase tracking-[0.15em] text-slate-400">
          {kind === "placement"
            ? "Issued when a student completes a placement programme."
            : "Issued for course-completion only (no placement workflow)."}
        </div>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-3 border-b border-slate-200 bg-slate-50">
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={addText} data-testid="btn-add-text" className="sms-btn-secondary text-xs inline-flex items-center gap-1"><Plus size={12} weight="bold" /> Add text</button>
          <button onClick={addSignature} data-testid="btn-add-signature" className="sms-btn-secondary text-xs inline-flex items-center gap-1"><Signature size={12} weight="duotone" /> Add signature</button>
          <button onClick={openLibrary} data-testid="btn-open-library" className="sms-btn-secondary text-xs inline-flex items-center gap-1"><Stack size={12} weight="duotone" /> Insert from library</button>
          <input ref={sigInputRef} type="file" accept="image/png,image/jpeg,image/webp" data-testid="sig-input" className="hidden" onChange={(e) => onSignaturePicked(e.target.files?.[0])} />
          <label className="sms-btn-secondary text-xs inline-flex items-center gap-1 cursor-pointer">
            <ImageSquare size={12} weight="duotone" /> {template.background_image_base64 ? "Replace background" : "Upload background"}
            <input type="file" accept="image/*" data-testid="bg-input" className="hidden" onChange={(e) => onBackground(e.target.files?.[0])} />
          </label>
          {template.background_image_base64 && (
            <button onClick={() => setTemplate((t) => ({ ...t, background_image_base64: null, show_default_frame: true }))} data-testid="btn-remove-bg" className="sms-btn-secondary text-xs inline-flex items-center gap-1 text-[var(--sms-danger)]">
              <X size={12} weight="bold" /> Remove bg
            </button>
          )}
          <button onClick={resetLayout} data-testid="btn-reset" className="sms-btn-secondary text-xs inline-flex items-center gap-1"><ArrowCounterClockwise size={12} weight="bold" /> Reset</button>
          <div className="inline-flex items-center gap-1 ml-2 pl-2 border-l border-slate-200" data-testid="grid-control">
            <GridFour size={12} weight={grid > 0 ? "duotone" : "regular"} className={grid > 0 ? "text-[var(--sms-primary)]" : "text-slate-400"} />
            <span className="font-mono-sms text-[10px] uppercase tracking-[0.15em] text-slate-500">Grid</span>
            <div className="flex border border-slate-200">
              {GRID_OPTIONS.map((g) => (
                <button
                  key={g.value}
                  type="button"
                  onClick={() => setGrid(g.value)}
                  data-testid={`grid-${g.value}`}
                  className={`px-2 py-0.5 text-[10px] font-mono-sms ${grid === g.value ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-50"}`}
                >
                  {g.label}
                </button>
              ))}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={previewPdf} data-testid="btn-preview-pdf" className="sms-btn-secondary text-xs inline-flex items-center gap-1"><FileArrowDown size={12} weight="bold" /> Preview PDF</button>
          <button onClick={save} disabled={saving} data-testid="btn-save-template" className="sms-btn-primary text-xs inline-flex items-center gap-1"><FloppyDisk size={12} weight="bold" /> {saving ? "Saving…" : "Save template"}</button>
        </div>
      </div>

      <div className="grid lg:grid-cols-[1fr_300px] gap-0">
        {/* Canvas */}
        <div className="p-5 bg-slate-100" onClick={() => setSelectedId(null)}>
          <div
            ref={canvasRef}
            data-testid="cert-canvas"
            onClick={(e) => e.stopPropagation()}
            className="relative bg-white shadow-lg mx-auto"
            style={{
              width: PAGE_W * scale,
              height: PAGE_H * scale,
              backgroundImage: template.background_image_base64 ? `url(${template.background_image_base64})` : undefined,
              backgroundSize: "cover",
              backgroundPosition: "center",
            }}
          >
            {grid > 0 && (
              <div
                data-testid="grid-overlay"
                aria-hidden="true"
                className="absolute inset-0 pointer-events-none opacity-40"
                style={{
                  backgroundImage:
                    `linear-gradient(to right, rgba(0,85,255,0.08) 1px, transparent 1px),` +
                    `linear-gradient(to bottom, rgba(0,85,255,0.08) 1px, transparent 1px)`,
                  backgroundSize: `${grid * scale}px ${grid * scale}px`,
                }}
              />
            )}
            {template.show_default_frame && !template.background_image_base64 && (
              <>
                <div className="absolute inset-3 border-2 border-[#B45309] pointer-events-none" />
                <div className="absolute inset-[14px] border border-[#B45309]/70 pointer-events-none" />
              </>
            )}
            {template.elements.map((el) => (
              <CanvasElement
                key={el.id}
                el={el}
                scale={scale}
                grid={grid}
                selected={el.id === selectedId}
                onSelect={() => setSelectedId(el.id)}
                onChange={(patch) => updateEl(el.id, patch)}
              />
            ))}
          </div>
          <div className="text-center mt-3 font-mono-sms text-[10px] uppercase tracking-[0.2em] text-slate-500">
            Drag any element to reposition · grab the corner to resize · click to edit{grid > 0 ? ` · snap ${grid}pt` : ""}
          </div>
        </div>

        {/* Inspector */}
        <div className="border-l border-slate-200 p-5 space-y-4 lg:max-h-[700px] lg:overflow-y-auto" data-testid="inspector">
          {!selected && (
            <div className="text-xs text-slate-500">
              Select an element on the canvas to edit it, or
              <button onClick={addText} className="ml-1 text-[var(--sms-primary)] underline">add a new text element</button>.
            </div>
          )}
          {selected && (
            <>
              <div className="flex items-center justify-between">
                <div className="font-mono-sms text-[10px] uppercase tracking-[0.2em] text-[var(--sms-primary)] inline-flex items-center gap-1">
                  {selected.kind === "image" ? <Signature size={11} weight="duotone" /> : <ArrowsOutCardinal size={11} weight="bold" />}
                  {selected.kind === "image" ? `Image · ${selected.label || "Signature"}` : "Text"}
                </div>
                <button onClick={() => deleteEl(selected.id)} data-testid="btn-delete-el" className="text-slate-400 hover:text-[var(--sms-danger)]"><Trash size={14} weight="bold" /></button>
              </div>
              {selected.kind === "image" ? (
                <>
                  <img src={selected.image_base64} alt="signature preview" className="border border-slate-200 bg-slate-50 max-w-full" style={{ maxHeight: 120, objectFit: "contain" }} />
                  <div>
                    <Lab>Label (for organisation)</Lab>
                    <input className="sms-input text-xs" data-testid="el-label" value={selected.label || ""} onChange={(e) => updateEl(selected.id, { label: e.target.value })} placeholder="Signature" />
                  </div>
                  <button
                    type="button"
                    onClick={replaceSignature}
                    className="sms-btn-secondary text-xs w-full inline-flex items-center justify-center gap-2"
                  >
                    <ImageSquare size={12} weight="duotone" /> Replace image
                  </button>
                  <div className="text-[10px] font-mono-sms uppercase tracking-[0.15em] text-slate-400">Hold Shift while resizing to keep aspect ratio.</div>
                </>
              ) : (
                <>
                  <div>
                    <Lab>Text</Lab>
                    <textarea rows={3} className="sms-input font-mono-sms text-xs" data-testid="el-text" value={selected.text} onChange={(e) => updateEl(selected.id, { text: e.target.value })} />
                  </div>
                  <div>
                    <Lab>Insert placeholder</Lab>
                    <div className="flex flex-wrap gap-1">
                      {PLACEHOLDERS.map((ph) => (
                        <button key={ph} type="button" onClick={() => insertPlaceholder(ph)} className="font-mono-sms text-[9px] uppercase tracking-[0.15em] px-1.5 py-1 border border-slate-200 hover:border-[var(--sms-primary)] hover:text-[var(--sms-primary)]">{ph}</button>
                      ))}
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <NumberField label="Font size" value={selected.font_size} min={6} max={120} step={1} onChange={(v) => updateEl(selected.id, { font_size: v })} testid="el-font-size" />
                    <div>
                      <Lab>Weight</Lab>
                      <div className="flex border border-slate-200">
                        {["normal", "bold"].map((w) => (
                          <button key={w} type="button" onClick={() => updateEl(selected.id, { font_weight: w })} data-testid={`el-weight-${w}`} className={`flex-1 px-2 py-1 text-[10px] uppercase font-mono-sms ${selected.font_weight === w ? "bg-slate-900 text-white" : "text-slate-600"}`}>{w}</button>
                        ))}
                      </div>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Lab>Colour</Lab>
                      <div className="flex items-center gap-2">
                        <input type="color" value={selected.color} onChange={(e) => updateEl(selected.id, { color: e.target.value })} data-testid="el-color" className="w-9 h-9 border border-slate-200 cursor-pointer" />
                        <input type="text" value={selected.color} onChange={(e) => updateEl(selected.id, { color: e.target.value })} className="sms-input font-mono-sms text-xs flex-1" />
                      </div>
                    </div>
                    <div>
                      <Lab>Align</Lab>
                      <div className="flex border border-slate-200">
                        {ALIGN_OPTIONS.map((a) => (
                          <button key={a.id} type="button" onClick={() => updateEl(selected.id, { align: a.id })} data-testid={`el-align-${a.id}`} className={`flex-1 px-2 py-1 text-[10px] font-mono-sms ${selected.align === a.id ? "bg-slate-900 text-white" : "text-slate-600"}`}>{a.label}</button>
                        ))}
                      </div>
                    </div>
                  </div>
                </>
              )}
              <div className={`grid gap-3 ${selected.kind === "image" ? "grid-cols-4" : "grid-cols-3"}`}>
                <NumberField label="X" value={selected.x} onChange={(v) => updateEl(selected.id, { x: v })} testid="el-x" />
                <NumberField label="Y" value={selected.y} onChange={(v) => updateEl(selected.id, { y: v })} testid="el-y" />
                <NumberField label="Width" value={selected.w} onChange={(v) => updateEl(selected.id, { w: Math.max(20, v) })} testid="el-w" />
                {selected.kind === "image" && (
                  <NumberField label="Height" value={selected.h || 60} onChange={(v) => updateEl(selected.id, { h: Math.max(20, v) })} testid="el-h" />
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {libraryOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-xl" onClick={() => setLibraryOpen(false)}>
          <div onClick={(e) => e.stopPropagation()} className="bg-white border border-slate-900 shadow-2xl w-full max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="image-library-modal">
            <div className="flex items-start justify-between p-6 border-b border-slate-200">
              <div>
                <div className="font-mono-sms text-xs uppercase tracking-[0.2em] text-[var(--sms-primary)]">Image library</div>
                <div className="font-display text-2xl font-black tracking-tighter mt-1">Insert from database</div>
                <p className="text-xs text-slate-500 mt-2">
                  Institute logo and active partner-company logos — click any to drop it on the canvas.
                </p>
              </div>
              <button type="button" onClick={() => setLibraryOpen(false)} className="text-slate-400 hover:text-slate-900" data-testid="close-library">
                <X size={20} weight="bold" />
              </button>
            </div>
            <div className="p-6">
              {libraryLoading && <div className="font-mono-sms text-xs uppercase tracking-[0.2em] text-slate-400">Loading…</div>}
              {!libraryLoading && library.length === 0 && (
                <div className="text-sm text-slate-500">
                  No images available. Upload an institute logo from <em>Institute Details</em> or add partner-company logos under <em>Partner Companies</em>.
                </div>
              )}
              {!libraryLoading && library.length > 0 && (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4" data-testid="image-library-grid">
                  {library.map((item) => (
                    <button
                      key={item.id}
                      type="button"
                      onClick={() => insertLibraryImage(item)}
                      data-testid={`library-item-${item.id}`}
                      className="group border border-slate-200 hover:border-[var(--sms-primary)] p-3 text-left transition"
                    >
                      <div className="aspect-video bg-slate-50 flex items-center justify-center mb-2">
                        <img src={item.image_base64} alt={item.label} className="max-w-full max-h-full object-contain" />
                      </div>
                      <div className="text-xs font-mono-sms uppercase tracking-[0.1em] text-slate-500">{item.kind}</div>
                      <div className="text-sm font-semibold truncate group-hover:text-[var(--sms-primary)]">{item.label}</div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------- Draggable / resizable element on the canvas ----------------
function CanvasElement({ el, scale, grid, selected, onSelect, onChange }) {
  const ref = useRef(null);
  const isImage = el.kind === "image";
  const startDrag = (e) => {
    e.stopPropagation();
    onSelect();
    const startX = e.clientX, startY = e.clientY;
    const origin = { x: el.x, y: el.y };
    const onMove = (ev) => {
      const dx = (ev.clientX - startX) / scale;
      const dy = (ev.clientY - startY) / scale;
      const nx = Math.max(0, Math.min(PAGE_W - 10, origin.x + dx));
      const ny = Math.max(0, Math.min(PAGE_H - 10, origin.y + dy));
      onChange({
        x: snap(nx, grid),
        y: snap(ny, grid),
      });
    };
    const onUp = () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  };
  const startResize = (e) => {
    e.stopPropagation();
    onSelect();
    const startX = e.clientX, startY = e.clientY;
    if (isImage) {
      const originW = el.w, originH = el.h || 60;
      const aspect = originW / originH;
      const onMove = (ev) => {
        const dx = (ev.clientX - startX) / scale;
        // Lock aspect ratio when shift held; otherwise free resize
        const nw = Math.max(40, originW + dx);
        const nh = ev.shiftKey ? nw / aspect : Math.max(20, originH + (ev.clientY - startY) / scale);
        onChange({
          w: snap(nw, grid),
          h: snap(nh, grid),
        });
      };
      const onUp = () => {
        window.removeEventListener("mousemove", onMove);
        window.removeEventListener("mouseup", onUp);
      };
      window.addEventListener("mousemove", onMove);
      window.addEventListener("mouseup", onUp);
      return;
    }
    const originW = el.w, originSize = el.font_size;
    const onMove = (ev) => {
      const dx = (ev.clientX - startX) / scale;
      const dy = (ev.clientY - startY) / scale;
      const nw = Math.max(40, originW + dx);
      const nfs = Math.max(6, Math.min(120, originSize + dy / 4));
      onChange({
        w: snap(nw, grid),
        font_size: grid >= 5 ? Math.round(nfs) : nfs,
      });
    };
    const onUp = () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  };

  return (
    <div
      ref={ref}
      data-testid={`canvas-el-${el.id}`}
      onMouseDown={startDrag}
      style={{
        position: "absolute",
        left: el.x * scale,
        top: el.y * scale,
        width: el.w * scale,
        height: isImage ? (el.h || 60) * scale : undefined,
        fontSize: !isImage ? el.font_size * scale : undefined,
        fontWeight: !isImage && el.font_weight === "bold" ? 700 : 400,
        color: !isImage ? el.color : undefined,
        textAlign: !isImage ? el.align : undefined,
        lineHeight: 1.25,
        cursor: "move",
        userSelect: "none",
        outline: selected ? "1.5px solid var(--sms-primary)" : "1px dashed transparent",
        outlineOffset: 2,
        whiteSpace: "pre-wrap",
        wordBreak: "break-word",
      }}
      className={selected ? "" : "hover:outline-slate-300"}
    >
      {isImage ? (
        <img
          src={el.image_base64}
          alt={el.label || "Image"}
          draggable={false}
          style={{ width: "100%", height: "100%", objectFit: "contain", pointerEvents: "none" }}
        />
      ) : (
        el.text || <span style={{ color: "#9CA3AF" }}>(empty)</span>
      )}
      {selected && (
        <span
          data-testid={`resize-${el.id}`}
          onMouseDown={startResize}
          className="absolute -bottom-1 -right-1 w-3 h-3 bg-white border-2 border-[var(--sms-primary)] cursor-nwse-resize"
        />
      )}
    </div>
  );
}

function Lab({ children }) {
  return <div className="font-mono-sms text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-1.5">{children}</div>;
}

function NumberField({ label, value, onChange, min, max, step = 1, testid }) {
  return (
    <div>
      <Lab>{label}</Lab>
      <input
        type="number"
        className="sms-input font-mono-sms text-xs"
        value={Math.round(value)}
        min={min}
        max={max}
        step={step}
        data-testid={testid}
        onChange={(e) => onChange(Number(e.target.value))}
      />
    </div>
  );
}
